"use client";

import { APP, handleLogin, PAGES, USER_TYPE } from "@idss/libraries";
import { handleError } from "@idss/notification";
import {
  Box,
  Button,
  Checkbox,
  Flex,
  PasswordInput,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { Form, useForm, yupResolver } from "@mantine/form";
import { modals } from "@mantine/modals";
import { useMutation } from "@tanstack/react-query";
import { useCookies } from "next-client-cookies";
import { boolean, object, string } from "yup";

import { builder } from "@/builders";
import { LoginResponseData } from "@/builders/types/login";
import { TwoFaAuth } from "@/components/shared/two-fa-auth";

import { navigate } from "@/actions/navigate";
import { cast } from "@/libraries/cast";
import { setCookie } from "cookies-next";

import Link from "next/link";

const schema = object({
  username: string()
    .email("Invalid email address")
    .required("Email is required"),
  password: string().required("Password is required"),
  remember_me: boolean().notRequired(),
});

export default function Page() {
  const cookies = useCookies();
  const username = cast.string(cookies.get(APP.EMAIL));

  const form = useForm({
    initialValues: {
      username,
      password: "",
      remember_me: false,
    },
    validate: yupResolver(schema),
    validateInputOnBlur: true,
  });

  const { mutate, isPending } = useMutation({
    mutationFn: builder.$use.auth.login,
    onSuccess: (payload) => {
      const { data, token, message, auth_2fa_type } = payload;

      function loginCallback(data: LoginResponseData) {
        const {
          access_token,
          user_type,
          organization_id,
          organization_unique_link,
        } = data;

        if (
          user_type === USER_TYPE.ADMIN ||
          user_type === USER_TYPE.SUPER_ADMIN
        ) {
          handleLogin({
            access_token,
            user_type,
            organization_id,
            organization_unique_link,
          });

          navigate(PAGES.DASHBOARD);
        } else {
          handleError({
            title: "Unauthorized",
            message: "Kindly sign in using your Organization's unique link",
          })();
        }
      }

      if (data) loginCallback(data);
      if (auth_2fa_type === "Authenticator") {
        modals.open({
          title: "Two Factor Authentication",
          children: (
            <TwoFaAuth
              token={token}
              message={message}
              email={form.values.username}
              callback={loginCallback}
            />
          ),
        });
      }
    },
    onError: handleError(),
  });

  function handleSubmit({ remember_me, ...values }: typeof form.values) {
    mutate(values, {
      onSettled: () => {
        if (remember_me) {
          setCookie("eMail", values.username, {
            maxAge: 60 * 60 * 24 * 365,
          });
        }
      },
    });
  }

  return (
    <Stack gap={24} justify="stretch" flex={1}>
      <h2 className="prose-3xl/medium text-primary-text-body">Sign in</h2>

      <Box component={Form} form={form} onSubmit={handleSubmit} w="100%">
        <Stack gap={50}>
          <Stack gap={16}>
            <Stack gap={30}>
              <TextInput
                type="email"
                label="Email"
                placeholder="sarmer"
                withAsterisk
                {...form.getInputProps("username")}
              />

              <PasswordInput
                label="Password"
                placeholder="*************"
                withAsterisk
                type="password"
                {...form.getInputProps("password")}
              />
            </Stack>
            <Flex justify="space-between" gap={8} align="center" wrap="wrap">
              <Checkbox
                label="Remember me"
                className="text-primary-text-subtle"
                {...form.getInputProps("remember_me", { type: "checkbox" })}
              />
              <Link href="/forgot-password">
                <Text className="text-base font-medium text-primary-button-normal">
                  Forget password?
                </Text>
              </Link>
            </Flex>
          </Stack>
          <Button loading={isPending} disabled={isPending} type="submit">
            Sign in
          </Button>
        </Stack>
      </Box>
    </Stack>
  );
}
